/**
 * Russian translations for Ключ-Астром MCP Chat Client
 */

import type { PluralForms } from './plural';

export const ru = {
  // Common
  common: {
    save: 'Сохранить',
    cancel: 'Отмена',
    delete: 'Удалить',
    edit: 'Редактировать',
    close: 'Закрыть',
    dismiss: 'Скрыть',
    search: 'Поиск',
    all: 'Все',
    loading: 'Загрузка...',
    error: 'Ошибка',
    success: 'Успешно',
    confirm: 'Подтвердить',
    back: 'Назад',
    next: 'Далее',
    add: 'Добавить',
    remove: 'Удалить',
    update: 'Обновить',
    refresh: 'Обновить',
    clear: 'Очистить',
    export: 'Экспорт',
    import: 'Импорт',
    settings: 'Настройки',
    help: 'Помощь',
    about: 'О программе',
    page: 'Страница',
    of: 'из',
    items: { one: 'запись', few: 'записи', many: 'записей', other: 'записи' } as PluralForms,
    previous: 'Назад',
    or: 'или',
    accept: 'Принять',
    reject: 'Отклонить',
  },

  // Navigation
  nav: {
    chat: 'Чат',
    settings: 'Настройки',
    analytics: 'Аналитика',
    tools: 'Инструменты',
    servers: 'Серверы',
  },

  // Chat Interface
  chat: {
    title: 'Чат Ключ-Астром MCP',
    newChat: 'Новый чат',
    placeholder: 'Введите сообщение...',
    send: 'Отправить',
    thinking: 'Думаю...',
    generating: 'Генерирую ответ...',
    noMessages: 'Нет сообщений. Начните беседу!',
    copyCode: 'Копировать код',
    copied: 'Скопировано!',
    // STORY-520: в режиме AUTO ответила другая модель (не часть текста ответа)
    failoverNote: '{failed} не ответила (ошибка на стороне провайдера); ответила {model}.',
    // STORY-520: AUTO ушёл с модели, но ни одна модель не ответила (все отказали или следующая отклонила запрос)
    failoverNoteNoAnswer: '{failed} не ответила (ошибка на стороне провайдера).',
    regenerate: 'Регенерировать',
    stop: 'Остановить',
    /** STORY-299: screen-reader text and title suffix of a tab whose chat is waiting on an answer. */
    tabPending: 'отвечает…',
    clearHistory: 'Очистить историю',
    clearHistoryConfirm: 'Вы уверены, что хотите очистить всю историю чата?',
    deleteMessage: 'Удалить сообщение',
    deleteMessageConfirm: 'Вы уверены, что хотите удалить это сообщение?',
    sessionTitle: 'Название сессии',
    renameSession: 'Переименовать сессию',
    deleteSession: 'Удалить сессию',
    deleteSessionConfirm: 'Вы уверены, что хотите удалить эту сессию?',
    pinSession: 'Закрепить сессию',
    unpinSession: 'Открепить сессию',
    exportSession: 'Экспортировать сессию',
    importSession: 'Импортировать сессию',
    // message actions, tool details, history (F-7)
    copyMessage: 'Копировать сообщение',
    copiedToClipboard: 'Скопировано в буфер обмена',
    copyFailed: 'Не удалось скопировать',
    copy: 'Копировать',
    copyJson: 'Копировать JSON',
    copyOutput: 'Копировать вывод',
    showDetails: 'Показать детали',
    hideDetails: 'Скрыть детали',
    arguments: 'Аргументы',
    output: 'Вывод',
    calledTimes: 'Вызван {count} с одинаковыми параметрами',
    tokens: 'Токены',
    jumpToLatest: 'К последнему сообщению',
    toolTimeline: 'Хронология инструментов',
    noData: 'Нет данных.',
    noMatchingRows: 'Нет подходящих строк на этой странице.',
    // Paged table (DynamicTable). The verb agrees with the number too:
    // «Показана 1 проблема», «Показаны 2 проблемы», «Показано 5 проблем».
    problemsLoaded: {
      one: 'Показана {loaded} проблема из {total}',
      few: 'Показаны {loaded} проблемы из {total}',
      many: 'Показано {loaded} проблем из {total}',
      other: 'Показано {loaded} проблемы из {total}',
    } as PluralForms,
    itemsLoaded: 'Показано {loaded} из {total}',
    loadingPage: 'Загрузка страницы {page}…',
    pageFetchFailed: 'Не удалось загрузить страницу',
    collectMore: 'Собрать ещё',
    collectMoreRemaining: 'Собрать ещё (осталось {remaining})',
    collecting: 'Собираю данные…',
    collectMoreFailed: 'Не удалось собрать дополнительные данные',
    loadingHistory: 'Загрузка истории…',
  },

  // Settings
  settings: {
    title: 'Настройки',
    appearance: 'Внешний вид',
    language: 'Язык',
    theme: 'Тема',
    themeLight: 'Светлая',
    themeDark: 'Тёмная',
    themeSystem: 'Системная',
    fontSize: 'Размер шрифта',
    fontSizeSmall: 'Маленький',
    fontSizeMedium: 'Средний',
    fontSizeLarge: 'Большой',
    codeTheme: 'Тема кода',
    autoSave: 'Автосохранение',
    showTimestamps: 'Показывать временные метки',
    enableNotifications: 'Включить уведомления',
    
    // LLM Provider Settings
    llmProvider: 'Провайдер LLM',
    llmProviders: 'Провайдеры LLM',
    addProvider: 'Добавить провайдера',
    editProvider: 'Редактировать провайдера',
    deleteProvider: 'Удалить провайдера',
    providerName: 'Название провайдера',
    providerType: 'Тип провайдера',
    providerModel: 'Модель',
    providerEnabled: 'Включено',
    apiKey: 'API ключ',
    apiUrl: 'URL API',
    maxTokens: 'Макс. токенов',
    temperature: 'Температура',
    temperaturePrecise: 'Точно (0)',
    temperatureBalanced: 'Сбалансированно (0.7)',
    temperatureCreative: 'Креативно (2)',
    topP: 'Top P',
    presencePenalty: 'Штраф за присутствие',
    frequencyPenalty: 'Штраф за частоту',
    llmMode: 'Режим LLM',
    llmModeAuto: 'Авто (Параллельно)',
    llmModeAutoDescription: 'Запускать все доступные провайдеры параллельно и объединять результаты',
    llmModeSingle: 'Один провайдер',
    llmModeSingleDescription: 'Использовать только выбранный провайдер',
    selectProvider: 'Выбрать провайдера',
    selectedProviderInfo: 'Выбранный провайдер будет использоваться для всех запросов чата',
    initializingProvider: 'Инициализация провайдера...',
    providerInitialized: 'Провайдер успешно инициализирован',
    providerInitializationFailed: 'Не удалось инициализировать провайдер',
    testingProvider: 'Тестирование провайдера...',
    providerTestSuccess: 'Тест провайдера успешен',
    providerTestFailed: 'Тест провайдера не удался',
    providerNotPersisted: 'Провайдер активен, но не сохранён в конфигурации — после перезапуска он будет потерян. Проверьте права на файлы бэкенда.',
    
    // MCP Server Settings
    mcpServers: 'MCP серверы',
    addServer: 'Добавить сервер',
    editServer: 'Редактировать сервер',
    deleteServer: 'Удалить сервер',
    serverName: 'Название сервера',
    serverUrl: 'URL сервера',
    serverTransport: 'Транспорт',
    serverEnabled: 'Включено',
    serverStatus: 'Статус',
    serverConnected: 'Подключено',
    serverDisconnected: 'Отключено',
    serverConnecting: 'Подключение...',
    serverError: 'Ошибка',
    testConnection: 'Проверить соединение',
    refreshServers: 'Обновить серверы',
    
    // Import/Export
    importExport: 'Импорт/Экспорт',
    exportSettings: 'Экспортировать настройки',
    importSettings: 'Импортировать настройки',
    exportAllSessions: 'Экспортировать все сессии',
    importSessions: 'Импортировать сессии',
    exportCurrentSession: 'Экспортировать текущую сессию',
    completeBackup: 'Полная резервная копия',
    chatSessions: 'Сессии чата',
    settingsOnly: 'Только настройки',
    exportAllDataDescription: 'Экспортировать все данные, включая сессии, настройки и конфигурации серверов',
    exportSessionsDescription: 'Экспортировать только историю чата и разговоры',
    exportSettingsDescription: 'Экспортировать MCP серверы, провайдеры LLM и настройки приложения',
    importData: 'Импорт данных',
    chooseFileToImport: 'Выбрать файл для импорта',
    importDescription: 'Выберите JSON файл, экспортированный из этого приложения. Импорт объединит данные с существующими.',
    importTooLarge: 'Файл резервной копии слишком большой: {size} МБ. Максимальный размер — {limit} МБ. Импорт не выполнялся.',
    importTooManySessions: 'Чатов в резервной копии: {actual}. За один импорт можно перенести не более {allowed}. Импорт не выполнялся.',
    importTooManyMessages: 'Сообщений в резервной копии: {actual}. За один импорт можно перенести не более {allowed}. Импорт не выполнялся.',
    importProgress: 'Импорт чатов: {done} из {total}…',
    importPartialFailure: 'Импортировано чатов: {imported} из {total}, затем импорт прервался: {error}. Файл в порядке — это сбой сервера или соединения. Уже импортированные чаты сохранены, повторный импорт того же файла создаст их копии.',
    importSettingsSkipped: 'Эти настройки из файла неизвестны или имеют недопустимое значение и не применены: {keys}.',
    currentDataSummary: 'Текущая сводка данных',
    chatSessionsLabel: 'Сессии чата:',
    mcpServersLabel: 'MCP серверы:',
    llmProvidersLabel: 'Провайдеры LLM:',
    totalMessagesLabel: 'Всего сообщений:',
    dangerZone: 'Опасная зона',
    clearAllData: 'Очистить все данные',
    clearAllDataConfirm: 'Вы уверены, что хотите очистить все данные? Это действие нельзя отменить.',
    clearAllDataWarning: 'Это навсегда удалит все ваши сессии чата, конфигурации серверов и настройки. Это действие нельзя отменить.',
    clearAllDataPartial: 'Удалено чатов: {deleted} из {total}. Не удалось удалить на сервере: {failed} — они снова появятся после перезагрузки страницы.',
    clearAllDataSettingsNotReset: 'Настройки не удалось сбросить на сервере.',
    importExportDescription: 'Резервное копирование и восстановление данных чата, настроек и конфигураций',
    
    // LLM Provider additional keys
    providerTypeGigachat: 'GigaChat',
    providerTypeOpenai: 'OpenAI',
    providerTypeAnthropic: 'Anthropic',
    providerTypeOllama: 'Ollama',
    providerTypeCustom: 'Пользовательский',
    endpointUrl: 'URL конечной точки',
    modelParameters: 'Параметры модели',
    active: 'Активен',
    activate: 'Активировать',
    // STORY-520: состояние провайдера по реальным вызовам
    providerFailing: 'Ошибки',
    providerFailingTitle: 'Вызовы этого провайдера завершаются ошибкой ({kind}, подряд: {count}, с {since}). Режим AUTO несколько минут сначала обращается к другим провайдерам; один успешный ответ снимает отметку.',
    providerErrorKindProviderError: 'ошибка на стороне провайдера',
    providerErrorKindTimeout: 'нет ответа вовремя',
    addNewProvider: 'Добавить нового провайдера',
    noProvidersConfigured: 'Нет настроенных провайдеров LLM',
    addProviderToEnable: 'Добавьте провайдера для включения функциональности чата',
    configureLanguageModels: 'Настройка провайдеров языковых моделей для функциональности чата',
    providerPlaceholder: 'Мой LLM провайдер',
    apiKeyPlaceholder: 'Ваш API ключ',
    endpointPlaceholder: 'https://api.example.com/v1',
    modelPlaceholder: 'напр. gpt-4o, claude-sonnet-4-20250514, GigaChat-Max',

    // Provider type selection
    selectProviderType: 'Выберите тип провайдера...',

    // GigaChat advanced settings
    gigachatBaseUrl: 'Базовый URL API',
    gigachatAuthUrl: 'URL авторизации OAuth',
    gigachatAdvanced: 'Расширенные настройки GigaChat',
    gigachatCertFile: 'Сертификат клиента (путь к PEM)',
    gigachatKeyFile: 'Ключ клиента (путь к PEM)',
    gigachatCaBundle: 'Корневой сертификат CA (путь к PEM)',
    gigachatCertHint: 'Для аутентификации mTLS. Оставьте пустым для аутентификации по ключу. PEM можно хранить в Vault.',

    // OpenAI advanced settings
    providerOpenaiAdvanced: 'Расширенные настройки OpenAI',
    providerOpenaiAdvancedHint: 'URL конечной точки выше можно изменить для использования OpenAI-совместимых API (напр., Azure OpenAI, локальные прокси).',

    // Anthropic advanced settings
    providerAnthropicAdvanced: 'Расширенные настройки Anthropic',
    providerAnthropicAdvancedHint: 'URL конечной точки выше можно изменить при использовании Anthropic-совместимого прокси.',

    // Cluster management
    clusters: 'Кластеры',
    clustersDescription: 'Управление подключениями к кластерам Astromkey',
    addCluster: 'Добавить кластер',
    addNewCluster: 'Добавить новый кластер',
    clusterName: 'Имя кластера',
    clusterUrl: 'URL кластера',
    noClusters: 'Нет настроенных кластеров',
    clusterBoundNotice: 'Мультикластер отключен — лицензия привязана к кластеру',
    verifySSL: 'Проверять SSL-сертификат',
    verifySSLHint: 'Отключите для самоподписанных сертификатов',
    caBundle: 'CA-сертификат (PEM)',
    uploadCaBundle: 'Загрузить PEM',
    caBundleUploaded: 'CA-сертификат загружен',
    caBundleHint: 'Для кластеров с внутренними CA-сертификатами',
    queryRouting: 'Маршрутизация запросов',
    routingSequential: 'Последовательно',
    routingParallel: 'Параллельно',
    clusterBase: 'базовый',
    sslOff: 'SSL выкл.',
    clusterTest: 'Проверить',
    inactive: 'Неактивен',
    healthApiReachable: 'API доступен',
    healthAuthFailed: 'Ошибка аутентификации (проверьте токены)',
    healthConnectionRefused: 'Соединение отклонено',
    healthTimedOut: 'Превышено время ожидания',
    healthHealthy: 'доступен',
    healthDegraded: 'ограничен',
    healthUnreachable: 'недоступен',
    healthUnknown: 'неизвестно',
    healthStatusOk: 'ок',
    healthStatusReady: 'готов',
    healthStatusError: 'ошибка',
    healthStatusDegraded: 'ограничен',
    healthStatusUnconfigured: 'не настроен',
    healthStatusInitializing: 'запуск',
    mcpClientConnected: 'подключён',
    mcpClientUnavailable: 'недоступен',

    // MCP Server additional keys
    transportSse: 'SSE (События, отправляемые сервером)',
    transportHttp: 'HTTP',
    transportWebsocket: 'WebSocket',
    authenticationToken: 'Токен аутентификации (необязательно)',
    authenticationTokenPlaceholder: 'Bearer токен или API ключ',
    noServersConfigured: 'Нет настроенных MCP серверов',
    addServerToEnable: 'Добавьте сервер для включения интеграции внешних инструментов',
    configureMCPDescription: 'Настройка серверов протокола управления моделями для интеграции внешних инструментов',
    serverPlaceholder: 'Мой MCP сервер',
    serverUrlPlaceholder: 'http://localhost:9001',
    backendStatus: 'Бэкенд',
    mcpStatus: 'MCP',
    checkedAt: 'Проверено:',
    
    // Appearance additional keys
    chatPreferences: 'Настройки чата',
    showMessageTimestamps: 'Показывать временные метки сообщений',
    autoSaveChatSessions: 'Автоматически сохранять сессии чата',
    enableDesktopNotifications: 'Включить уведомления на рабочем столе',
    codeThemeVsDark: 'VS Code Dark',
    codeThemeGithub: 'GitHub Light',
    codeThemeMonokai: 'Monokai',
    codeThemeDracula: 'Dracula',
    languageAffectsLLM: 'Изменение языка также влияет на ответы LLM',
    customizeLookAndFeel: 'Настройка внешнего вида и ощущений интерфейса чата',
    
    // Vault Settings
    vault: 'Секреты Vault',
    vaultDescription: 'Управление секретами и сертификатами для MCP сервисов',
    vaultStatus: 'Статус Vault',
    vaultConnected: 'Подключено',
    secretsManagement: 'Секреты',
    llmProvidersHint: 'Учетные данные LLM-провайдеров (GigaChat, OpenAI, Anthropic, Ollama) настраиваются во вкладке "LLM Провайдеры". Здесь только инфраструктурные секреты.',
    reloadAllServices: 'Перезагрузить все сервисы',
    updateSecret: 'Обновить',
    
    // Secret categories
    mcpSecrets: 'Конфигурация MCP',
    chatSecrets: 'Конфигурация чата',
    ollamaSecrets: 'Конфигурация Ollama',
    emailSecrets: 'Email и уведомления',
    telegramSecrets: 'Telegram бот',

    // Secret labels
    clusterBaseUrl: 'URL кластера',
    clusterApiToken: 'Токен API кластера',
    environmentApiToken: 'Токен API окружения',
    gigachatCredentials: 'Учетные данные GigaChat',
    gigachatModel: 'Модель GigaChat',
    gigachatScope: 'Область GigaChat',
    vaultGigachatBaseUrl: 'Базовый URL API GigaChat',
    vaultGigachatAuthUrl: 'URL авторизации OAuth GigaChat',
    vaultGigachatVerifySsl: 'Проверка SSL GigaChat',
    vaultGigachatCertFile: 'Сертификат клиента GigaChat (путь к файлу)',
    vaultGigachatKeyFile: 'Ключ клиента GigaChat (путь к файлу)',
    vaultGigachatCaBundleFile: 'CA сертификат GigaChat (путь к файлу)',
    vaultGigachatKeyFilePassword: 'Пароль ключа GigaChat',
    vaultGigachatCertPem: 'Сертификат клиента GigaChat (PEM содержимое)',
    vaultGigachatKeyPem: 'Ключ клиента GigaChat (PEM содержимое)',
    vaultGigachatCaBundlePem: 'CA сертификат GigaChat (PEM содержимое)',
    mcpServerUrl: 'URL MCP сервера',
    ollamaEnabled: 'Ollama включен',
    ollamaBaseUrl: 'URL Ollama',
    ollamaModels: 'Модели Ollama',
    ollamaDefaultModel: 'Модель по умолчанию',
    // Email / SMTP
    smtpVerifyEmail: 'Верификация email',
    smtpHost: 'SMTP хост',
    smtpPort: 'SMTP порт',
    smtpUser: 'SMTP логин',
    smtpPassword: 'SMTP пароль',
    smtpFrom: 'Адрес отправителя',
    smtpUseTls: 'Использовать TLS',
    appBaseUrl: 'Базовый URL приложения',
    // Telegram
    telegramBotToken: 'Токен бота',
    
    // Certificates
    certificates: 'Сертификаты',
    certificateStatus: 'Статус сертификата',
    certificateValid: 'Действителен',
    certificateExpired: 'Истёк',
    certificateExpiry: 'Истекает',
    daysRemaining: 'дней осталось',
    certificateSource: 'Источник',
    noCertificate: 'Нет сертификата',
    uploadCertificate: 'Загрузить сертификат',
    storeInVault: 'Хранить в Vault ',
    vaultStorageHint: 'Сертификат будет храниться в Vault KV',
    fileStorageHint: 'Сертификат будет сохранён в файловую систему',

    // Extension Certificates
    extensionCertificates: 'Сертификаты расширения',
    extensionCertDescription: 'Управление сертификатами расширения для увеличения лимитов API или сроков действия',
    noExtensionCerts: 'Сертификаты расширения не установлены',
    uploadExtensionCert: 'Загрузить сертификат расширения',
    revokeExtensionCert: 'Отозвать',
    extensionType: 'Тип',
    apiLimit: 'Лимит API',
    extensionDays: 'Дней расширения',
    certValidFrom: 'Действителен с',
    certValidUntil: 'Действителен до',
    certRevoked: 'Отозван',
    certificateInMcp: 'MCP-сервер',
    certOtherInMcp: 'Работает с другим сертификатом',
    certActive: 'Действует',
    certInactive: 'Не применяется',
    certStatusUnknown: 'Статус MCP недоступен',
    certError: 'Ошибка',
    confirmRevoke: 'Вы уверены, что хотите отозвать этот сертификат расширения? Это действие необратимо.',

    // Vault warnings
    vaultNotEnabled: 'Vault не включен',
    vaultNotEnabledHint: 'Секреты загружаются из переменных окружения. Чтобы включить Vault, установите VAULT_ENABLED=true и настройте VAULT_ADDR и VAULT_TOKEN.',
    vaultNotConfigured: 'Не настроено',
    vaultDisconnected: 'Отключен',
    vaultDisconnectedHint: 'Vault настроен, но недоступен. Проверьте статус Vault и перезапустите сервис для повторного подключения.',
    vaultNotConfiguredHint: 'Задайте VAULT_ADDR и VAULT_TOKEN, затем перезапустите сервис.',
    vaultAuthFailed: 'Ошибка аутентификации',
    vaultAuthFailedHint: 'Vault доступен, но отклонил токен. Обновите VAULT_TOKEN и перезапустите сервис.',
    vaultErrorClientNotInitialized: 'Клиент Vault не инициализирован (задайте VAULT_ADDR и VAULT_TOKEN)',
    vaultErrorAuthFailed: 'Vault отклонил токен',
    vaultErrorCouldNotConnect: 'Не удалось связаться с Vault',
    vaultErrorDisabled: 'Vault выключен',

    // Secret field status
    secretSource: 'Источник',
    secretSourceEnvironment: 'окружение',
    secretSourceVault: 'Vault',
    secretSourceUnknown: 'неизвестно',
    secretNotConfigured: 'Не настроено',
    secretEnabled: 'Включено',
    secretDisabled: 'Отключено',
    clickToEnable: 'Нажмите, чтобы включить',
    clickToDisable: 'Нажмите, чтобы выключить',
    maxApiCalls: 'Макс. вызовов API',
    certificateSourceFile: 'файл',

    // Environment file security
    envFileSecurity: 'Безопасность файла окружения',
    envFileSecurityHint: 'Когда все секреты сохранены в Vault, чувствительные значения можно удалить из файла .env.',
    addToVault: 'Добавить в Vault',
    setAllSecretsHint: 'Сначала сохраните все секреты в Vault, затем очистите файл .env',
    clearEnvConfirm: 'Все секретные значения будут удалены из файла .env. Секреты останутся только в Vault. Это действие сложно отменить. Продолжить?',
    confirmClear: 'Да, очистить секреты',
    clearEnvSecrets: 'Очистить секреты из .env',
    secretMigrated: 'Секрет «{key}» перенесён в Vault.',
    envSecretsCleared: 'Очищено секретов из .env: {count}. Значения теперь хранятся только в Vault.',
    envSecretsClearedMcpKept: 'Очищено секретов из .env чат-сервиса: {count}; теперь чат-сервис читает их из Vault. Файл .env MCP-сервера оставлен как есть — причина ниже.',
    envClearMcpVaultOff: 'Файл {path} не изменён: у MCP-сервера собственный Vault не включён, и токены кластера он читает из этого файла.',
    envClearMcpUnreachable: 'Файл {path} не изменён: не удалось связаться с MCP-сервером и убедиться, что он читает эти секреты из Vault.',
    envClearFileNotFound: 'Файла .env MCP-сервера нет по пути {path} — там очищать нечего.',
    envClearFileFailed: 'Не удалось перезаписать {path} — секреты в нём остались.',
    envClearBackupsLeft: 'Не удалось очистить резервные копии {names} — удалите их вручную: в них могут оставаться секреты.',
    envClearLegacyKept: 'Не изменены (заглушка прежней очистки, а в Vault значения нет): {keys}. Перенесите их в Vault и очистите снова.',
    
    // Token Usage tab
    tokenUsage: 'Использование токенов',

    // Toast messages
    secretSaved: 'Секрет сохранён',
    reloadSuccess: 'Сервисы перезагружены',
    certificateUploaded: 'Сертификат загружен',

    // LLM provider management
    providerDeactivationFailed: 'Не удалось отключить провайдера',
    secretAlreadySet: 'Ключ уже настроен. Введите значение только для его замены.',

    // Shared settings
    settingsApplied: 'Общие настройки применены',
    settingsRejected: 'Общие настройки отклонены',
    sharedWithYou: 'С вами поделились настройками',
    settingsShared: 'Настройки успешно отправлены',
    shareSettings: 'Поделиться настройками',
    selectSettings: 'Выберите настройки для отправки',
    selectAdmins: 'Поделиться с',
    share: 'Поделиться',
  },

  // STORY-289: counted nouns of the settings screens. Not inside `settings`:
  // VaultSettings indexes that section by key and needs plain strings.
  settingsCounts: {
    toolsAvailable: { one: 'инструмент доступен', few: 'инструмента доступно', many: 'инструментов доступно', other: 'инструмента доступно' } as PluralForms,
    daysRemaining: { one: 'день остался', few: 'дня осталось', many: 'дней осталось', other: 'дня осталось' } as PluralForms,
  },

  // Tools
  tools: {
    title: 'Инструменты MCP',
    availableTools: 'Доступные инструменты',
    noTools: 'Нет доступных инструментов',
    toolName: 'Название инструмента',
    toolDescription: 'Описание инструмента',
    toolParameters: 'Параметры',
    executeTool: 'Выполнить инструмент',
    executing: 'Выполнение...',
    executed: 'Выполнено',
    executionFailed: 'Выполнение не удалось',
    result: 'Результат',
    error: 'Ошибка',
    noParameters: 'Нет параметров',
    requiredParameter: 'Обязательный параметр',
    optionalParameter: 'Необязательный параметр',
    parameterType: 'Тип',
    parameterDefault: 'По умолчанию',
    favoriteTools: 'Избранные инструменты',
    addToFavorites: 'Добавить в избранное',
    removeFromFavorites: 'Удалить из избранного',
  },

  // Timeline
  timeline: {
    title: 'История инструментов',
    toolInvoked: 'Инструмент вызван',
    duration: 'Длительность',
    status: 'Статус',
    arguments: 'Аргументы',
    noEvents: 'Нет событий в истории',
    clearTimeline: 'Очистить историю',
  },

  // Errors
  errors: {
    generic: 'Произошла ошибка',
    network: 'Ошибка сети',
    timeout: 'Превышено время ожидания',
    unauthorized: 'Не авторизован',
    forbidden: 'Доступ запрещён',
    notFound: 'Не найдено',
    serverError: 'Ошибка сервера',
    invalidInput: 'Неверный ввод',
    missingParameter: 'Отсутствует обязательный параметр',
    connectionFailed: 'Не удалось подключиться',
    executionFailed: 'Не удалось выполнить',
    messageTooLong: 'Сообщение слишком длинное: его длина {length}, а принимается не более {max} символов. Отправьте сообщение короче — из лога достаточно значимой части.',
  },

  // Success messages
  success: {
    saved: 'Сохранено успешно',
    deleted: 'Удалено успешно',
    updated: 'Обновлено успешно',
    connected: 'Подключено успешно',
    disconnected: 'Отключено успешно',
    exported: 'Экспортировано успешно',
    imported: 'Импортировано успешно',
    copied: 'Скопировано в буфер обмена',
  },

  // Authentication
  auth: {
    loginTitle: 'Вход',
    registerTitle: 'Регистрация',
    username: 'Имя пользователя',
    usernamePlaceholder: 'Введите имя пользователя',
    email: 'Электронная почта',
    emailPlaceholder: 'you@example.com',
    password: 'Пароль',
    loginButton: 'Войти',
    registerButton: 'Создать аккаунт',
    logout: 'Выйти',
    noAccount: 'Нет аккаунта?',
    hasAccount: 'Уже есть аккаунт?',
    registerLink: 'Создать',
    loginLink: 'Войти',
    loginFailed: 'Неверный email или пароль',
    registerFailed: 'Ошибка регистрации. Попробуйте ещё раз.',
    emailNotVerifiedError: 'Ваш email ещё не подтверждён. Проверьте почту или запросите повторную отправку ссылки.',
    // Email verification
    checkYourEmail: 'Проверьте почту',
    verificationSent: 'Мы отправили ссылку для подтверждения на:',
    verificationHint: 'Перейдите по ссылке в письме для подтверждения адреса. Проверьте папку «Спам», если письмо не пришло.',
    resendVerification: 'Отправить письмо повторно',
    resendFailed: 'Не удалось отправить письмо повторно',
    verifyingEmail: 'Подтверждение email...',
    emailVerified: 'Email подтверждён!',
    emailVerifiedHint: 'Ваш адрес электронной почты подтверждён. Теперь вы можете войти.',
    verificationFailed: 'Ошибка подтверждения',
    accountCreated: 'Аккаунт создан!',
    accountCreatedHint: 'Ваш аккаунт создан. Теперь вы можете войти.',
    goToLogin: 'Перейти ко входу',
    // Forgot / Reset password
    forgotPassword: 'Забыли пароль?',
    forgotPasswordTitle: 'Сброс пароля',
    forgotPasswordHint: 'Введите email, привязанный к вашему аккаунту, и мы отправим ссылку для сброса пароля.',
    forgotPasswordFailed: 'Не удалось отправить письмо для сброса пароля',
    sendResetLink: 'Отправить ссылку',
    resetPasswordTitle: 'Новый пароль',
    resetPasswordHint: 'Введите новый пароль ниже.',
    newPassword: 'Новый пароль',
    confirmPassword: 'Подтвердите пароль',
    passwordMismatch: 'Пароли не совпадают',
    passwordTooShort: 'Пароль должен быть не менее 6 символов',
    resetPasswordButton: 'Сбросить пароль',
    resetPasswordFailed: 'Не удалось сбросить пароль. Возможно, ссылка устарела.',
    passwordResetSuccess: 'Пароль изменён!',
    passwordResetSuccessHint: 'Ваш пароль был изменён. Теперь вы можете войти с новым паролем.',
    ssoLogin: 'Войти через SSO',
    ssoFailed: 'Не удалось войти через SSO. Попробуйте ещё раз.',
    ssoAccountDisabled: 'Вход через SSO отклонён: учётная запись деактивирована.',
    ssoEmailNotVerified: 'Вход через SSO отклонён: поставщик удостоверений не подтвердил этот адрес e-mail.',
    showPassword: 'Показать пароль',
    hidePassword: 'Скрыть пароль',
  },

  // Admin
  admin: {
    userManagement: 'Управление пользователями',
    totalUsers: { one: 'пользователь', few: 'пользователя', many: 'пользователей', other: 'пользователя' } as PluralForms,
    searchPlaceholder: 'Поиск по имени или email…',
    role: 'Роль',
    emailStatus: 'Email',
    status: 'Статус',
    lastLogin: 'Последний вход',
    actions: 'Действия',
    active: 'Активен',
    deactivated: 'Деактивирован',
    emailVerified: 'Подтверждён',
    emailNotVerified: 'Не подтверждён',
    deactivate: 'Деактивировать',
    activate: 'Активировать',
    deactivateUser: 'Деактивировать пользователя',
    activateUser: 'Активировать пользователя',
    page: 'Страница',
    noUsers: 'Пользователи не найдены',
    loadFailed: 'Не удалось загрузить пользователей',
    actionFailed: 'Не удалось выполнить действие',
    changeRole: 'Изменить роль',
    verifyEmailTitle: 'Подтвердить email вручную',
    clickToVerify: 'Нажмите для ручного подтверждения email',
    verify: 'Подтвердить',
    createUser: 'Создать пользователя',
    createUserTitle: 'Создать пользователя',
    create: 'Создать',
    userCreated: 'Пользователь создан',
    budget: 'Лимит',
    budgetTitle: 'Месячный лимит токенов',
    budgetPlaceholder: 'Лимит токенов (пусто = без лимита)',
    budgetUnlimited: 'Без лимита',
    usedThisMonth: 'Использовано за месяц',
    resetPasswordTitle: 'Сбросить пароль',
    newPassword: 'Новый пароль',
    you: 'вы',
    roleAdmin: 'Администратор',
    roleUser: 'Пользователь',
    roleViewer: 'Наблюдатель',
    deactivateConfirm: 'Деактивировать «{username}» ({email})? Пользователь не сможет войти в систему.',
    activateConfirm: 'Активировать «{username}» ({email})?',
    verifyEmailConfirm: 'Подтвердить email для «{username}» ({email}) вручную?',
  },

  // Welcome / Landing Page
  welcome: {
    subtitle: 'Интеллектуальный мониторинг производительности приложений на базе Model Context Protocol. Прогнозируйте проблемы, отслеживайте метрики и получайте аналитику с помощью ИИ — всё через диалоговый интерфейс.',
    getStarted: 'Начать',
    featurePredictiveTitle: 'Предиктивная аналитика',
    featurePredictiveDesc: 'Прогнозирование трендов производительности и обнаружение аномалий до того, как они повлияют на пользователей.',
    featureMetricsTitle: 'Мониторинг метрик',
    featureMetricsDesc: 'Отслеживание CPU, памяти, задержек запросов и сотен метрик инфраструктуры в реальном времени.',
    featureAlertsTitle: 'Подписки на оповещения',
    featureAlertsDesc: 'Настройте пользовательские пороги и получайте уведомления, когда системам требуется внимание.',
    featureChatTitle: 'ИИ-чат',
    featureChatDesc: 'Задавайте вопросы на естественном языке и получайте мгновенные ответы на основе данных инфраструктуры через инструменты MCP.',
    footer: 'Платформа Ключ-Астром MCP',
  },

  // Command Palette
  commandPalette: {
    placeholder: 'Введите команду... (например, /get_runtime_license)',
    noCommands: 'Нет подходящих команд',
  },

  // Modal
  modal: {
    close: 'Закрыть модальное окно',
    confirm: 'Подтвердить действие',
    cancel: 'Отменить действие',
  },

  // Tool Invocation Modal
  toolModal: {
    title: 'Выполнить инструмент',
    serverLabel: 'Сервер',
    toolLabel: 'Инструмент',
    parametersLabel: 'Параметры',
    executeButton: 'Выполнить',
    executingButton: 'Выполнение...',
    closeButton: 'Закрыть',
    resultLabel: 'Результат',
    errorLabel: 'Ошибка',
    noParametersMessage: 'У этого инструмента нет параметров',
    addToChatButton: 'Добавить в чат',
  },

  // Languages
  languages: {
    en: 'English',
    ru: 'Русский',
  },

  // Date/Time
  dateTime: {
    now: 'сейчас',
    minutesAgo: (n: number) => `${n} ${n === 1 ? 'минута' : n < 5 ? 'минуты' : 'минут'} назад`,
    hoursAgo: (n: number) => `${n} ${n === 1 ? 'час' : n < 5 ? 'часа' : 'часов'} назад`,
    daysAgo: (n: number) => `${n} ${n === 1 ? 'день' : n < 5 ? 'дня' : 'дней'} назад`,
    weeksAgo: (n: number) => `${n} ${n === 1 ? 'неделя' : n < 5 ? 'недели' : 'недель'} назад`,
  },

  // Scheduled Queries
  scheduledQueries: {
    title: 'Запланированные запросы',
    description: 'Автоматизируйте повторяющиеся запросы по расписанию',
    create: 'Создать',
    createQuery: 'Создать запланированный запрос',
    editQuery: 'Редактировать запланированный запрос',
    confirmSaveTitle: 'Подтвердите запланированный запрос',
    confirmSaveMessage: 'Проверьте запланированный запрос перед сохранением.',
    confirmSaveButton: 'Подтвердить и сохранить',
    name: 'Название',
    namePlaceholder: 'Мой ежедневный отчёт',
    queryText: 'Текст запроса',
    queryPlaceholder: 'Покажи хосты с загрузкой CPU > 80%',
    mode: 'Режим',
    modeQuery: 'Запрос',
    modeScenario: 'Сценарий',
    scenario: 'Сценарий',
    scenarioPlaceholder: 'Выберите сценарий',
    scenarioEmpty: 'Нет доступных сценариев',
    scenarioLoadError: 'Не удалось загрузить сценарии',
    schedule: 'Расписание',
    customCron: 'Использовать cron выражение',
    usePreset: 'Использовать шаблон',
    notification: 'Уведомления',
    notifyInApp: 'В приложении',
    notifyTelegram: 'Telegram',
    lastRun: 'Последний',
    nextRun: 'Следующий',
    runNow: 'Запустить сейчас',
    history: 'История',
    deactivate: 'Деактивировать',
    activate: 'Активировать',
    noQueries: 'Нет запланированных запросов',
    noQueriesHint: 'Создайте свой первый запланированный запрос для автоматизации.',
    noResult: 'Пока нет результатов',
    // In-chat scheduling
    scheduleThis: 'Запланировать',
    scheduleThisTooltip: 'Запланировать автоматическое выполнение этого запроса',
    scheduleModalTitle: 'Запланировать запрос',
    scheduleModalSubtitle: 'Запуск по расписанию с автоматическими уведомлениями',
    scheduled: 'Запланировано!',
    scheduledHint: 'Запрос будет выполняться по расписанию',
    editableInSettings: 'Редактировать в Настройки → Запланированные запросы',
    willRun: 'Будет выполняться',
    advancedOptions: 'Дополнительные настройки',
    queryTextEditable: 'Текст запроса (редактируемый)',
    cronFormat: 'Формат: мин час день месяц деньнед',
    pickFromPresets: 'Выбрать из шаблонов',
    useCustomCronExpression: 'Использовать cron выражение',
    remove: 'Удалить',
    deleteConfirmTitle: 'Удалить запланированный запрос?',
    deleteConfirmHint: 'Это навсегда удалит расписание и всю историю выполнений. Отменить действие будет невозможно.',
    deleting: 'Удаление...',
    historyOf: 'История',
    noHistory: 'История выполнений пока пуста.',
    telegramNotificationsEnabled: 'Уведомления в Telegram включены',
    statusSuccess: 'Успешно',
    statusError: 'Ошибка',
    statusRunning: 'Выполняется',
    cronPresets: {
      everyHour: 'Каждый час',
      every6Hours: 'Каждые 6 часов',
      dailyAt9am: 'Ежедневно в 9:00',
      dailyAtMidnight: 'Ежедневно в полночь',
      everyMondayAt9am: 'Каждый понедельник в 9:00',
      everyWeekdayAt9am: 'Будни в 9:00',
      firstOfMonthAt9am: '1-е число месяца в 9:00',
    },
  },

  // Telegram
  telegram: {
    title: 'Telegram',
    description: 'Привяжите аккаунт Telegram для получения уведомлений и общения с ИИ через Telegram',
    status: 'Статус подключения',
    linked: 'Подключён',
    notLinked: 'Не подключён',
    notLinkedHint: 'Сгенерируйте токен для привязки аккаунта',
    unlink: 'Отвязать аккаунт',
    generateToken: 'Сгенерировать токен',
    linkInstructions: 'Инструкции по привязке',
    openInTelegram: 'Открыть в Telegram',
    howItWorks: 'Как это работает',
    feature1: 'Сообщения боту обрабатываются как ваши авторизованные запросы',
    feature2: 'Результаты запланированных запросов могут отправляться в Telegram',
    feature3: 'Ваш аккаунт защищён — бот проверяет вашу личность через токен привязки',
    botNotConfigured: 'Telegram-бот не настроен',
    botNotConfiguredHint: 'Установите переменную окружения TELEGRAM_BOT_TOKEN для включения Telegram-бота.',
  },

  // Token Usage Statistics
  stats: {
    title: 'Статистика использования токенов',
    today: 'Сегодня',
    thisWeek: 'За неделю',
    thisMonth: 'За месяц',
    allTime: 'Всё время',
    last7Days: 'Последние 7 дней',
    last30Days: 'Последние 30 дней',
    last90Days: 'Последние 90 дней',
    requests: 'запросов',
    requestsCount: { one: 'запрос', few: 'запроса', many: 'запросов', other: 'запроса' } as PluralForms,
    tokens: 'Токены',
    cost: 'Стоимость',
    byProvider: 'По провайдерам',
    byModel: 'По моделям',
    dailyBreakdown: 'По дням',
    date: 'Дата',
    provider: 'Провайдер',
    model: 'Модель',
    promptTokens: 'Запрос',
    completionTokens: 'Ответ',
    totalTokens: 'Всего',
    noData: 'Нет данных',
    noDataDescription: 'Статистика использования токенов появится здесь по мере использования чата.',
  },
  // Analytics
  analytics: {
    title: 'Аналитика',
    sessionCost: 'Стоимость сессии',
    toolCalls: 'Вызовы инструментов',
    contextWindow: 'Контекстное окно',
    performance: 'Производительность',
    usageTrends: 'Тренды использования',
    allUsers: 'Все пользователи',
    scheduledSummary: 'Сводка расписаний',
    tokens: 'токенов',
    cost: 'Стоимость',
    requests: 'запросов',
    totalCalls: 'Всего вызовов',
    uniqueTools: 'Уникальных инструментов',
    blockedDuplicates: 'Заблокировано дубликатов',
    saved: 'сэкономлено',
    efficiency: 'Эффективность',
    topTools: 'Топ инструментов',
    callsPerDay: 'Вызовов в день',
    contextUsage: 'Использование контекста',
    turns: 'ходов',
    entities: 'сущностей',
    avgResponseTime: 'Ср. время ответа',
    p95Latency: 'P95 задержка',
    byModel: 'По моделям',
    dailyTrend: 'Тренд по дням',
    model: 'Модель',
    avgMs: 'Ср. мс',
    p95Ms: 'P95 мс',
    requestCount: 'Запросов',
    activeSchedules: 'Активных расписаний',
    runsThisWeek: 'Запусков за неделю',
    failures: 'Ошибок',
    successRate: 'Успешность',
    totalRuns: 'Всего запусков',
    today: 'Сегодня',
    yesterday: 'Вчера',
    thisWeek: 'За неделю',
    thisMonth: 'За месяц',
    noData: 'Нет данных',
    loading: 'Загрузка...',
    fetchError: 'Не удалось загрузить данные',
    refresh: 'Обновить',
    collapse: 'Свернуть',
    expand: 'Развернуть',
    viewAll: 'Подробнее',
    recipeHitRate: 'Частота рецептов',
    recipeName: 'Рецепт',
    hitCount: 'Попадания',
    avgDuration: 'Ср. время',
    lastFired: 'Последний',
    noRecipeHits: 'Рецепты ещё не срабатывали',
    providerVolume: 'Объём по провайдерам',
    noQueriesLogged: 'Запросы ещё не зарегистрированы',
    queries: 'запросов',
    toolFrequency: 'Частота инструментов',
    noToolCalls: 'Вызовы инструментов ещё не зарегистрированы',
    callCount: 'Вызовов',
    blockRate: 'Блокировки',
    days7: '7 дней',
    days30: '30 дней',
    ms: 'мс',
    seconds: 'с',
    daysShort: 'дн.',
    reqShort: 'запр.',
    avgAbbrev: 'средн.',
    // STORY-289: counted nouns, picked with Intl.PluralRules (i18n/plural.ts)
    counts: {
      tokens: { one: 'токен', few: 'токена', many: 'токенов', other: 'токена' } as PluralForms,
      toolCalls: { one: 'вызов', few: 'вызова', many: 'вызовов', other: 'вызова' } as PluralForms,
      uniqueTools: { one: 'уникальный инструмент', few: 'уникальных инструмента', many: 'уникальных инструментов', other: 'уникального инструмента' } as PluralForms,
      recipes: { one: 'рецепт', few: 'рецепта', many: 'рецептов', other: 'рецепта' } as PluralForms,
      hits: { one: 'попадание', few: 'попадания', many: 'попаданий', other: 'попадания' } as PluralForms,
      queries: { one: 'запрос', few: 'запроса', many: 'запросов', other: 'запроса' } as PluralForms,
      providers: { one: 'провайдер', few: 'провайдера', many: 'провайдеров', other: 'провайдера' } as PluralForms,
      blockedDuplicates: { one: 'заблокированный дубликат', few: 'заблокированных дубликата', many: 'заблокированных дубликатов', other: 'заблокированного дубликата' } as PluralForms,
      calls: { one: 'вызов', few: 'вызова', many: 'вызовов', other: 'вызова' } as PluralForms,
      schedules: { one: 'расписание', few: 'расписания', many: 'расписаний', other: 'расписания' } as PluralForms,
      active: { one: 'активное', few: 'активных', many: 'активных', other: 'активного' } as PluralForms,
      activeSchedules: { one: 'активное расписание', few: 'активных расписания', many: 'активных расписаний', other: 'активного расписания' } as PluralForms,
      runsThisWeek: { one: 'запуск за неделю', few: 'запуска за неделю', many: 'запусков за неделю', other: 'запуска за неделю' } as PluralForms,
      failures: { one: 'ошибка', few: 'ошибки', many: 'ошибок', other: 'ошибки' } as PluralForms,
      totalRuns: { one: 'запуск всего', few: 'запуска всего', many: 'запусков всего', other: 'запуска всего' } as PluralForms,
      turns: { one: 'ход', few: 'хода', many: 'ходов', other: 'хода' } as PluralForms,
      hiddenDuplicates: { one: 'дубликат скрыт', few: 'дубликата скрыто', many: 'дубликатов скрыто', other: 'дубликата скрыто' } as PluralForms,
      times: { one: 'раз', few: 'раза', many: 'раз', other: 'раза' } as PluralForms,
      failedClusters: { one: 'кластер с ошибкой', few: 'кластера с ошибкой', many: 'кластеров с ошибкой', other: 'кластера с ошибкой' } as PluralForms,
      splitQueries: { one: 'разделённый запрос', few: 'разделённых запроса', many: 'разделённых запросов', other: 'разделённого запроса' } as PluralForms,
      parts: { one: 'часть', few: 'части', many: 'частей', other: 'части' } as PluralForms,
    },
    topPrefix: 'Топ',
    requestTimedOut: 'Превышено время ожидания ответа',
    // STORY-301: панель статистики составных запросов (только для администратора)
    compound: {
      title: 'Статистика составных запросов',
      adminOnly: 'Админ',
      period: 'Период',
      days1: '1 день',
      days7: '7 дней',
      days30: '30 дней',
      days90: '90 дней',
      empty: 'За этот период составных запросов не было.',
      classifierOff: 'Классификатор составных запросов выключен, измерять нечего. Чтобы собирать эту статистику, включите COMPOUND_CLASSIFIER_ENABLED.',
      sectionTypes: 'Типы запросов',
      classifiedByLlm: 'Классификация LLM',
      coverage: 'Как классификатор обработал эти запросы',
      coverageTotal: 'Все запросы',
      coverageScreened: 'Отсеяны фильтром признаков, без вызова LLM',
      coverageLlm: 'Классифицированы LLM',
      coverageShadow: 'Из них в теневом режиме (выполнены как один запрос)',
      coverageOff: 'Классификатор выключен',
      coverageError: 'Ошибка классификатора (выполнены как один запрос)',
      coverageUnrecorded: 'Статус не записан',
      coverageNote: '«Выполненный план» учитывает все запросы, которые обработал классификатор, с вызовом LLM и без: отсеянные фильтром признаков выполняются как одиночные (или как последовательные шаги, если их спланировали встроенные правила) и ниже учтены как простые. Запросы при выключенном классификаторе плана не имеют и в эти таблицы не входят.',
      executedPlan: 'Выполненный план',
      // STORY-297: кто спланировал выполненное последовательное разбиение
      plannedBy: 'Кто спланировал последовательные шаги',
      planSources: {
        classifier: 'LLM-классификатор',
        tier1: 'Встроенные правила (без LLM)',
        other: 'Другое',
      },
      sectionFailures: 'Сбои частей',
      noSplits: 'За этот период ни один запрос не был разделён на части.',
      noFailures: 'За этот период ни одна часть не завершилась сбоем и не была пропущена.',
      failRate: 'Доля сбоев',
      failed: 'Сбой',
      skipped: 'Пропущено',
      skippedHint: 'Пропущенные части не запускались (следующий шаг, у которого первый шаг завершился сбоем или ничего не нашёл) и не считаются сбоями.',
      byReason: 'По причине',
      byPosition: 'По части',
      byDomain: 'По области',
      recentFailures: 'Последние части со сбоем',
      legacyHint: 'У частей, записанных до этой версии, причина не сохранена.',
      sectionLatency: 'Добавленная задержка',
      baselineSimple: 'По сравнению с одиночными запросами за тот же период',
      baselineRecipe: 'По сравнению с ответами рецептов без классификации',
      noBaseline: 'За этот период нет одиночных запросов для сравнения, поэтому добавленную задержку вычислить нельзя.',
      colLabel: 'Название',
      colCount: 'Количество',
      colShare: 'Доля',
      colTime: 'Время',
      colPlan: 'План',
      colPart: 'Часть',
      colDomain: 'Область',
      colReason: 'Причина',
      colDuration: 'Длительность',
      colP50: 'p50',
      colP95: 'p95',
      colExtraP50: 'Добавлено p50',
      colExtraP95: 'Добавлено p95',
      byClass: 'По классу',
      byPlan: 'По плану',
      partPrefix: 'Часть',
      otherLabel: 'Другое',
      statusFail: 'сбой',
      statusSkipped: 'пропущено',
      classes: {
        simple: 'Простой',
        parallel: 'Параллельный',
        sequential: 'Последовательный',
        compare: 'Сравнение',
        ambiguous: 'Неоднозначный',
        other: 'Другое',
      },
      plans: {
        single: 'Одиночный',
        parallel_n: 'Параллельные части',
        sequential: 'Последовательные шаги',
        compare: 'Сравнение',
        other: 'Другое',
      },
      reasons: {
        timeout: 'Превышено время ожидания',
        cancelled: 'Отменено',
        exception: 'Завершилось ошибкой',
        no_result: 'Нет результата',
        budget_blocked: 'Исчерпан бюджет токенов',
        provider_error: 'Ошибка LLM-провайдера',
        content_policy: 'Отказ по политике содержания',
        confidential_refusal: 'Отказ: конфиденциальные данные',
        providers_unavailable: 'Все LLM-провайдеры недоступны',
        context_overflow: 'Слишком большой контекст',
        validation_error: 'Ошибка валидации',
        error_path: 'Ошибка внутри части (см. журнал)',
        unexpected_path: 'Неожиданный путь ответа',
        upstream_failed: 'Пропущено: сбой предыдущего шага',
        no_followup_refs: 'Пропущено: нечего уточнять',
        unrecorded: 'Причина не записана (до этой версии)',
        other: 'Другое',
      },
      domains: {
        problems: 'Проблемы',
        entities: 'Сущности',
        metrics: 'Метрики',
        logs: 'Логи',
        k8s: 'Kubernetes',
        dashboards: 'Дашборды',
        rca: 'Анализ корневой причины',
        unknown: 'Не определено',
      },
    },
  },

  // Configuration wizard (first-run setup)
  configuration: {
    title: 'Настройка системы',
    subtitle: 'Для начала работы настройте подключение к кластеру и LLM-провайдеру.',
    clusterSection: 'Подключение к кластеру',
    llmSection: 'LLM-провайдер',
    clusterUrl: 'URL кластера',
    clusterUrlPlaceholder: 'https://your-cluster.example.com/e/environment-id',
    clusterToken: 'Cluster API Token',
    clusterTokenPlaceholder: 'dt0c01.XXXXXXXX.XXXXXXXX',
    envToken: 'Environment API Token',
    envTokenPlaceholder: 'dt0c01.XXXXXXXX.XXXXXXXX',
    gigachatCredentials: 'GigaChat Credentials (Base64)',
    gigachatPlaceholder: 'YzI4YjY4ZGUtNDk4OS00NDEy...',
    ollamaUrl: 'Ollama Base URL',
    ollamaPlaceholder: 'http://localhost:11434',
    testConnection: 'Проверить',
    saveAndApply: 'Сохранить и применить',
    saveAndContinue: 'Сохранить и продолжить',
    continueToLlm: 'Перейти к настройке LLM',
    backToCluster: 'Назад',
    finishSetup: 'Завершить настройку',
    saving: 'Сохранение...',
    testing: 'Проверка...',
    testSuccess: 'Подключение успешно',
    testFailed: 'Ошибка подключения',
    saveSuccess: 'Конфигурация сохранена! Перезагрузка...',
    saveFailed: 'Не удалось сохранить конфигурацию',
    configured: 'Настроено',
    notConfigured: 'Не настроено',
    alreadyConfigured: 'Подключение к кластеру уже настроено. Вы можете обновить значения или перейти к настройке LLM.',
    optional: 'Необязательно',
    atLeastOneLlm: 'Добавьте хотя бы один LLM-провайдер для работы чат-ассистента.',
    finishBlockedLlm: 'Активный LLM-провайдер пока не обнаружен. Активируйте провайдер выше и повторите попытку.',
    finishBlockedCluster: 'LLM-провайдер готов, но подключение к кластеру не настроено. Вернитесь к шагу 1 и укажите URL кластера и токены.',
    maskPlaceholder: '****',
    step1Label: '1. Кластер',
    step2Label: '2. LLM-провайдер',
    clusterAlreadyConfigured: 'Учетные данные кластера уже настроены через MCP-сервер. Вы можете пропустить этот шаг или ввести новые значения.',
    // Shown to non-admins instead of the setup page (only admins can configure).
    notConfiguredTitle: 'Система ещё не настроена',
    notConfiguredBody: 'Подключение к кластеру или LLM-провайдер пока не заданы, поэтому ответы в чате могут быть недоступны.',
    notConfiguredContactAdmin: 'Обратитесь к администратору, чтобы завершить настройку.',
  },
  clusterMode: {
    pill: 'Кластеры',
    single: 'Один кластер',
    singleDesc: 'Самый быстрый. Запрос к одному кластеру.',
    sequential: 'Все — последовательно',
    sequentialDesc: 'Запрос к каждому кластеру по очереди.',
    sequentialShort: 'последовательно',
    parallel: 'Все — параллельно',
    parallelDesc: 'Запрос ко всем кластерам одновременно.',
    parallelShort: 'параллельно',
    selectClusters: 'Выберите кластеры:',
    activeCluster: 'Активный кластер:',
    defaultClusterShort: 'по умолчанию',
    noActiveClusterHint: 'Кластер не выбран — будет использован кластер по умолчанию.',
    synthesis: 'Объединить ответы с финальным резюме',
    synthesisOnDesc: 'Дополнительный LLM-вызов для объединения ответов кластеров.',
    synthesisOffDesc: 'Показать ответы каждого кластера отдельно (быстрее).',
    selectionUpdated: 'Выбор кластеров обновлён — больше недоступны: ',
    // STORY-300: упоминание кластера через @ в сообщении
    mentionListLabel: 'Кластеры для упоминания',
    mentionChip: 'Кластер',
    mentionChipTitle: 'Ответ получен с кластера, указанного через @ в вопросе',
  },

  pagination: {
    expired: 'Результаты устарели',
    expiredHint: 'Пожалуйста, выполните запрос повторно для получения актуальных данных.',
  },

  alerts: {
    title: 'Правила оповещений',
    newRule: 'Новое правило',
    editRule: 'Редактировать правило',
    deleteConfirm: 'Удалить это правило и все его события?',
    noRules: 'Пока нет правил оповещений',
    noRulesHint: 'Создайте первое правило для мониторинга.',
    noEvents: 'Событий оповещений пока не записано.',
    firing: 'Активен',
    resolved: 'Разрешен',
    rules: 'Правила',
    events: 'События',
    severity: 'Критичность',
    condition: 'Условие',
    threshold: 'Порог',
    interval: 'Интервал проверки',
    channels: 'Каналы уведомлений',
    metricQuery: 'Запрос метрики',
    ruleKind: 'Вид',
    kindMetric: 'Изменение метрик',
    kindSlo: 'Нарушение SLO',
    service: 'Сервис',
    servicePlaceholder: 'ExampleService',
    serviceHint: 'Пусто — все SLO',
    serviceAll: 'Все SLO',
    entitySelector: 'Фильтр сущностей',
    description: 'Описание',
    lastEvaluated: 'Последняя проверка',
    enabled: 'Включено',
    disabled: 'Отключено',
    enabledOfTotal: '{enabled} включено / {total}',
    totalRules: { one: 'правило всего', few: 'правила всего', many: 'правил всего', other: 'правила всего' } as PluralForms,
    firingCount: { one: 'срабатывает', few: 'срабатывают', many: 'срабатывают', other: 'срабатывают' } as PluralForms,
    critical: 'Критический',
    warning: 'Предупреждение',
    info: 'Информация',
    // form fields
    name: 'Название',
    webhookUrl: 'URL вебхука',
    save: 'Сохранить правило',
    saving: 'Сохранение...',
    cancel: 'Отмена',
    validationRequired: 'Название, запрос метрики и порог обязательны.',
    validationNameRequired: 'Название обязательно.',
    saveFailed: 'Не удалось сохранить правило',
    namePlaceholder: 'Оповещение CPU',
    sloNamePlaceholder: 'Нарушение SLO',
    metricQueryPlaceholder: 'builtin:host.cpu.usage — или вопрос: Какова максимальная загрузка CPU среди хостов?',
    thresholdPlaceholder: '80',
    webhookPlaceholder: 'https://example.com/webhook',
    descriptionPlaceholder: 'Необязательное описание',
    entitySelectorPlaceholder: 'type("HOST")',
    // condition operator labels
    condGt: '> (больше)',
    condLt: '< (меньше)',
    condGte: '>= (больше или равно)',
    condLte: '<= (меньше или равно)',
    condEq: '== (равно)',
    condNeq: '!= (не равно)',
    // notification channel labels
    chInApp: 'В приложении',
    chTelegram: 'Telegram',
    chEmail: 'Email',
    chWebhook: 'Вебхук',
    // evaluation interval labels
    int30s: '30 секунд',
    int1m: '1 минута',
    int5m: '5 минут',
    int10m: '10 минут',
    int30m: '30 минут',
    int1h: '1 час',
    // write confirmation (STORY-155, human-in-the-loop)
    confirmSaveTitle: 'Подтвердите изменение правила',
    confirmSaveMessage: 'Проверьте изменение перед сохранением.',
    confirmSaveButton: 'Подтвердить и сохранить',
    confirmDeleteTitle: 'Подтвердите удаление',
    confirmDeleteButton: 'Подтвердить и удалить',
    back: 'Назад',
    // rule details, events, icon-only controls (F-3)
    enableRule: 'Включить правило',
    disableRule: 'Отключить правило',
    deleteRule: 'Удалить правило',
    showEvents: 'Показать события правила',
    hideEvents: 'Скрыть события правила',
    intervalEvery: 'Интервал: каждые {seconds} с',
    recentEvents: 'Последние события',
    // STORY-275: an alert rule the chat proposed — saved only from the card
    proposalTitle: 'Предложенное правило оповещения',
    proposalHint: 'Правило ещё не создано. Оно сохранится от вашего имени только после подтверждения.',
    proposalCreate: 'Создать правило…',
    proposalConfirmTitle: 'Создать правило оповещения?',
    proposalConfirmMessage: 'Будет сохранено ровно это правило. Отмена ничего не записывает.',
    proposalCreated: 'Правило «{name}» создано — оно на странице «Правила оповещений».',
    proposalAlreadyCreated: 'Такое правило уже есть: «{name}» — оно на странице «Правила оповещений». Повторно не создаётся.',
    proposalDefault: 'по умолчанию',
    proposalAllHosts: 'все хосты (без фильтра)',
    proposalAllSlos: 'все SLO (без фильтра)',
    proposalEvery: 'каждые {seconds} с',
    resolvedAt: 'разрешено',
    // event rows formatted from condition/threshold (language-switchable)
    eventFiringMsg: '{name}: {value} {condition} {threshold}',
    eventResolvedMsg: '{name}: разрешён ({value} больше не {condition} {threshold})',
    ambiguousResponse: 'Неоднозначный ответ — сравнение не выполнено',
    skipSloRecipe: 'Рецепт SLO не прочитан',
    skipSloList: 'Ответ не список SLO',
    skipSlo: 'Проверка SLO пропущена',
  },
  hitl: {
    title: 'Операции записи, ожидающие подтверждения',
    approve: 'Подтвердить',
    deny: 'Отклонить',
    expiresIn: 'истекает через',
    argsUnavailable: 'параметры не сохранены здесь — повторите запрос',
    approvedResult: 'Подтверждено и выполнено:',
    deniedResult: 'Отклонено — ничего не записано:',
    dismiss: 'Скрыть',
  },
  promptHints: {
    title: 'Попробуйте один из примеров',
    examples: [
      'Покажи все хосты',
      'Какие проблемы открыты сейчас?',
      'Покажи CPU и память для самых загруженных хостов',
      'У каких сервисов больше всего ошибок за последний час?',
    ],
  },
  dispatch: {
    title: 'Настройка промптов',
    subtitle: 'Добавляйте свои формулировки, описания и подсказки, чтобы ассистент выбирал нужный инструмент. Встроенные правила не изменяются — только дополняются.',
    scope: 'Область',
    scopeRecipePattern: 'Формулировка рецепта (regex)',
    scopeRecipeDescription: 'Описание рецепта (для классификатора)',
    scopeSpecialistAddendum: 'Дополнение к промпту специалиста',
    target: 'Цель',
    targetRecipe: 'Рецепт',
    targetSpecialist: 'Специалист',
    language: 'Язык',
    payload: 'Значение',
    payloadPatternHint: 'Регулярное выражение (как во встроенных рецептах)',
    payloadDescriptionHint: 'Текст описания, который видит классификатор второго уровня',
    payloadAddendumHint: 'Текст, добавляемый в конец системного промпта специалиста',
    enabled: 'Включено',
    add: 'Добавить',
    create: 'Сохранить переопределение',
    delete: 'Удалить',
    confirmDelete: 'Удалить это переопределение?',
    existingOverrides: 'Действующие переопределения',
    noOverrides: 'Переопределений пока нет',
    custom: 'Своё',
    builtin: 'Встроенное',
    loadFailed: 'Не удалось загрузить переопределения',
    actionFailed: 'Не удалось выполнить действие',
    created: 'Переопределение сохранено',
    deleted: 'Переопределение удалено',
    testerTitle: 'Тест маршрутизации',
    testerSubtitle: 'Введите запрос и посмотрите, какой рецепт его обработает — до и после чернового переопределения.',
    testQuery: 'Тестовый запрос',
    testQueryPlaceholder: 'например: топ 5 хостов по CPU',
    runTest: 'Проверить',
    before: 'Сейчас',
    after: 'После изменения',
    matchedRecipe: 'Выбранный рецепт',
    noMatch: 'Нет совпадения (запрос уйдёт на LLM-классификатор / агентный путь)',
    matchedPattern: 'Сработавшая формулировка',
    specificity: 'Специфичность',
    tier: 'Уровень',
    blockedByGuard: 'Заблокировано защитой у рецептов',
    validationValid: 'Проверка пройдена — можно сохранять',
    validationInvalid: 'Отклонено проверкой',
    reasonRegexError: 'Ошибка в регулярном выражении',
    reasonTooBroad: 'Слишком широкий шаблон',
    reasonMatchesEmpty: 'Шаблон совпадает с пустой строкой (ловит всё)',
    reasonSecret: 'Обнаружен секрет/чувствительные данные',
    reasonTooLong: 'Слишком длинное значение',
    reasonUnknownTarget: 'Неизвестная цель',
    reasonBadLanguage: 'Недопустимый язык',
    reasonSteals: 'Шаблон перехватывает уже работающий запрос',
    draftHint: 'Черновик проверяется автоматически при вводе значения ниже.',
    descTitle: 'Описания инструментов MCP',
    descSubtitle: 'Влияйте на выбор инструмента в агентном режиме: добавляйте текст к описанию инструмента или заменяйте его. Только описание — параметры и схемы не меняются. Сохраняется на хост MCP и применяется «на лету», без перезапуска.',
    descNone: 'Переопределений описаний пока нет.',
    descToolName: 'имя инструмента',
    descTextHint: 'текст описания для добавления / замены',
    descAppend: 'добавить',
    descReplace: 'заменить',
    descMultilingual: 'многоязычная запись — редактируйте в файле',
    descSave: 'Сохранить и применить',
    descSaved: 'Сохранено и применено',
    descReload: 'Перечитать с диска',
    descReloaded: 'Описания перечитаны с диска',
  },
  // STORY-298: the interactive chart under a chart answer
  chart: {
    region: 'Интерактивный график',
    legend: 'Ряды графика',
    toggleSeries: 'Показать или скрыть ряд «{label}»',
    lastSeries: 'Последний видимый ряд скрыть нельзя',
    plotHint: 'Стрелки влево и вправо — по точкам, вверх и вниз — по рядам, «+» — приблизить, «0» — сбросить масштаб. Мышью: выделите диапазон, чтобы приблизить.',
    resetZoom: 'Сбросить масштаб',
    savePng: 'Сохранить как PNG',
    saving: 'Сохраняю…',
    savedSvg: 'Браузер не разрешил PNG — график сохранён как SVG',
    saveFailed: 'Не удалось сохранить график',
    period: 'за {period}',
    zoomed: 'Увеличено: {from} – {to}',
    zoomedOf: 'Увеличено: {from} – {to} (весь период: {period})',
    truncated: 'показаны {shown} из {total}',
    fullListInText: 'Все {total} — в тексте ответа выше.',
    downsampled: 'точки усреднены: {from} → {to}',
    countUnits: {
      hosts: { one: 'хост', few: 'хоста', many: 'хостов', other: 'хоста' } as PluralForms,
      points: { one: 'точка', few: 'точки', many: 'точек', other: 'точки' } as PluralForms,
    },
    bucket: 'интервал',
    noValue: 'нет данных',
    moreSeries: 'и ещё {n}',
    point: 'точка {n}',
  },
  // STORY-304: действия под карточкой инцидента
  incidentCard: {
    actions: 'Действия с карточкой инцидента',
    download: 'Скачать .md',
    downloadLabel: 'Скачать карточку инцидента файлом Markdown',
    downloaded: 'Карточка сохранена: {name}',
    print: 'Печать / PDF',
    printLabel: 'Распечатать карточку инцидента или сохранить её в PDF',
    sendTelegram: 'В Telegram',
    sendTelegramLabel: 'Отправить карточку инцидента в привязанный чат Telegram',
    sending: 'Отправка…',
    sent: 'Карточка отправлена в Telegram',
    sendFailed: 'Карточка не отправлена: {message}',
  },
  // STORY-303: user knowledge bases
  knowledge: {
    title: 'База знаний',
    subtitle: 'Документы, на которые опирается ассистент при ответах. Подходящие фрагменты передаются модели вместе с вопросом, а источник указывается под ответом.',
    listTitle: 'Документы',
    empty: 'Документов пока нет.',
    loading: 'Загрузка…',
    colTitle: 'Название',
    colScope: 'Доступ',
    colCluster: 'Кластер',
    colChunks: 'Фрагменты',
    colDate: 'Добавлен',
    colActions: 'Действия',
    allClusters: 'все кластеры',
    scopeShared: 'Общий',
    scopePersonal: 'Личный',
    scopeLabel: 'Кому виден документ',
    scopeSharedHint: 'Общий — виден всем пользователям',
    scopePersonalHint: 'Личный — виден только вам',
    personalOnlyNote: 'Ваши документы видны только вам.',
    uploadTitle: 'Добавить документ',
    titleLabel: 'Название документа',
    sourceMode: 'Как добавить',
    modePaste: 'Вставить текст',
    modeFile: 'Загрузить файл',
    contentLabel: 'Текст документа',
    contentPlaceholder: 'Вставьте текст регламента, инструкции или заметки…',
    fileLabel: 'Файл (.txt, .md, .log)',
    fileChosen: 'Выбран файл: {name}',
    fileUnsupported: 'Поддерживаются только текстовые файлы .txt, .md и .log.',
    fileTooLarge: 'Файл слишком большой: максимум {limit} КБ.',
    upload: 'Добавить',
    uploading: 'Добавление…',
    uploaded: 'Документ «{title}» добавлен: фрагментов — {chunks}.',
    redactedNotice: 'Перед сохранением скрыты персональные данные: {kinds}.',
    blocked: 'Документ не сохранён: в нём найдено — {kinds}. Уберите эти данные и попробуйте снова.',
    deleteLabel: 'Удалить документ «{title}»',
    deleteTitle: 'Удалить документ?',
    deleteConfirm: 'Документ «{title}» и все его фрагменты будут удалены без возможности восстановления.',
    deleted: 'Документ удалён.',
    searchTitle: 'Проверить поиск',
    searchHint: 'Покажет фрагменты, которые получит модель на такой вопрос.',
    searchLabel: 'Вопрос',
    searchButton: 'Найти',
    searchNone: 'Подходящих фрагментов нет — ответ будет дан без базы знаний.',
    error: 'Не удалось выполнить действие: {message}',
    kinds: {
      secret: 'секрет (токен, ключ или пароль)',
      email: 'адрес электронной почты',
      card: 'номер карты',
      ssn: 'номер социального страхования',
      iban: 'номер счёта IBAN',
    },
  },
};

export type Translation = typeof ru;

